/**
 * App Context Provider
 * Phase 2: Product Shell & Navigation
 * 
 * Manages application-wide state including:
 * - Current project context
 * - Current audit context
 * - Sidebar collapsed state
 */

import { createContext, useContext, useState, ReactNode, useEffect } from "react";

// ============================================================================
// Type Definitions
// ============================================================================

interface ProjectContext {
  id: string;
  name: string;
}

interface AuditContext {
  id: string;
  name: string;
  projectId?: string;
}

interface AppContextValue {
  // Project Context
  currentProject: ProjectContext | null;
  setCurrentProject: (project: ProjectContext | null) => void;

  // Audit Context
  currentAudit: AuditContext | null;
  setCurrentAudit: (audit: AuditContext | null) => void;

  // UI State
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
}

// ============================================================================
// Context Creation
// ============================================================================

const AppContext = createContext<AppContextValue | undefined>(undefined);

// ============================================================================
// Storage Keys
// ============================================================================

const STORAGE_KEY_SIDEBAR_COLLAPSED = "reviewer:sidebar-collapsed";

// ============================================================================
// Provider Component
// ============================================================================

interface AppContextProviderProps {
  children: ReactNode;
}

export function AppContextProvider({ children }: AppContextProviderProps) {
  // Project and Audit Context
  const [currentProject, setCurrentProject] = useState<ProjectContext | null>(null);
  const [currentAudit, setCurrentAudit] = useState<AuditContext | null>(null);

  // Sidebar State (persisted to localStorage)
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SIDEBAR_COLLAPSED);
      return stored === "true";
    } catch {
      return false;
    }
  });

  // Persist sidebar state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SIDEBAR_COLLAPSED, sidebarCollapsed.toString());
    } catch {
      // Ignore storage errors
    }
  }, [sidebarCollapsed]);

  // Helper to toggle sidebar
  const toggleSidebar = () => {
    setSidebarCollapsedState((prev) => !prev);
  };

  // Wrapper to persist sidebar state
  const setSidebarCollapsed = (collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
  };

  const value: AppContextValue = {
    currentProject,
    setCurrentProject,
    currentAudit,
    setCurrentAudit,
    sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// ============================================================================
// Hook for Using App Context
// ============================================================================

export function useAppContext(): AppContextValue {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within AppContextProvider");
  }
  return context;
}
